
import img0 from './Images/AddJdbcClasspath.png';
import img1 from './Images/GenerateJdbcUserToken.png';
  
const content = `# OnData JDBC Driver Installation and Configuration Guide

The OnData database drivers can be used to enforce data access policies for sensitive data. The data access roles, just like application user roles, can be assigned to a user 
when the user signs into applications through an Identity and Access Management (IAM) system. Such data access roles are embedded in users' tokens. When such tokens are 
passed to an OnData database driver, the driver will validate them, identify the data access roles explicitly granted to a user, and enforce the data access permissions 
so that the user can only retrieve or store data that the user is granted access to do so.  

The OnData JDBC driver (**com.ondata.jdbc**) is a Java JDBC API implementation that can access any kind of tabular data, especially data stored in a relational database. It implements 
the interfaces in the **java.sql** package and enables the dynamic encryption and decryption of sensitive data. Minimal code changes are required to pass user tokens to the 
OnData database driver.

OnData JDBC Driver features:

- Data Encryption: For table columns with encryption rules configured at the OnData Portal, data will be encrypted before being stored to database. The strong 
encryption significantly enhances the protection of the sensitive data against both external and internal threats.
- Data Decryption: If a user does have access to the encrypted data, such data will be seamlessly decrypted for the user. Otherwise, the data will stay as encrypted or masked 
per the masking rules configured at the OnData Portal.

## 1. OnData JDBC Driver Package
### 1.1 Windows Platform
The OnData JDBC Driver release package for Windows includes the following files:
* ondata-jdbc-<version_number>-jar-with-dependencies.jar - The JAR file for the OnData JDBC driver
* OnDataDriver.config – The configuration file for the OnData JDBC Driver;
* EncryptSQL.dll - The encryption module;
* Configurator.exe - The OnData Configurator tool for client registration;
* libcrypto-1_1-x64.dll – One of the three OpenSSL library files;
* libpq.dll – One of the three OpenSSL library files;
* libssl-1_1-x64.dll – One of the three OpenSSL library files;
* gdroot-g2.crt - The root certificate from the GoDaddy Class 2 Certification Authority.

Please follow the steps below to configure the OnData JDBC Driver on a computer where the driver will be used (this does not have to be a database server):  

* Copy the following files from the OnData JDBC Driver release package to a local folder, such as C:&bsol;OnData&bsol;OnDataJDBC&bsol;;
  * ondata-jdbc-<version_number>-jar-with-dependencies.jar
  * OnDataDriver.config
  * EncryptSQL.dll - The encryption module;
  * Configurator.exe
  * gdroot-g2.crt

* Copy the following OpenSSL files from the OnData JDBC Driver release package to the %WINDIR%&bsol;system32 folder, such as C:&bsol;Windows&bsol;system32&bsol;;
  * libcrypto-1_1-x64.dll
  * libpq.dll
  * libssl-1_1-x64.dll

* Update the OnData JDBC Driver configuration file (OnDataDriver.config) with information about the database type and the associated database engine driver. The Database
  Type shall be consistent with the database engine to be used for running your database. The list of database engines supported include MSSQL, ORACLE, POSTGRESQL, SNOWFLAKE, 
  DB2, MYSQL, and MARIADB for now. Populate the DriverFullPath entry with the location of the corresponding commercial database JDBC driver. Below is a sample configuration 
  to use the Microsoft SQL Server JDBC driver to connect to a SQL Server database:  

  \`\`\`bash
  DatabaseType = MSSQL
  DriverFullPath = file:/C:/driverjars/mssql-jdbc-9.4.1.jre11.jar
  \`\`\`

***Note:*** The system default GoDaddy root certificate will be used by default. If the JDBC driver cannot find such root certificate according to the driver log file, you need 
to copy the \`gdroot-g2.crt\` file to the path where the application that loads the JDBC driver is running from. You can find the running path from the driver log file as well.  


### 1.2 Linux and MacOS Platforms
The OnData JDBC Driver release package for the Linux and MacOS platforms (either arm64 or x64 processors) includes the following folders and files:
* ondata-jdbc-<version_number>-jar-with-dependencies.jar - The JAR file for the OnData JDBC driver
* OnDataDriver.config – The configuration file for the OnData JDBC Driver;
* EncryptSQL.so - The encryption module;
* Configurator-<version_number> - The OnData Configurator tool for client registration;
* gdroot-g2.crt - The root certificate from the GoDaddy Class 2 Certification Authority.

Please follow the steps below to configure the OnData JDBC Driver on a computer where the driver will be used (this does not have to be a database server):  

* Install the OpenSSL library that the OnData JDBC Driver depends on from the command line (such as \`sudo apt-get install libssl\`);  
  
* Copy all files from the OnData JDBC Driver release package to the Linux library folder (such as \`/usr/lib64/\` on Oracle Linux, or \`/usr/lib/x86_64-linux-gnu/jdbc/\` folder on Ubuntu);  
 
* Update the OnData JDBC Driver configuration file (OnDataDriver.config) with information about the data source and the associated database engine driver. The Database
  Type shall be consistent with the database engine to be used for running your database. The list of database engines supported include MSSQL, ORACLE, POSTGRESQL, SNOWFLAKE, 
  DB2, MYSQL, and MARIADB for now. Populate the DriverFullPath entry with the location of the corresponding commercial database driver. Below is a sample configuration to use 
  the Oracle driver to connect to an Oracle database:  

  \`\`\`bash
  DatabaseType = ORACLE
  DriverFullPath = file:/usr/lib64/jdbc/ojdbc11-21.5.0.0.jar
  \`\`\`

***Note:*** The system default GoDaddy root certificate will be used by default. If the JDBC driver cannot find such root certificate according to the driver log file, you need 
to copy the \`gdroot-g2.crt\` file to either the \`/usr/share/certs\` folder or the path where the application that loads the JDBC driver is running from. You can find the running path 
from the driver log file as well.  


### 1.3 Register OnData JDBC Driver as OnData Client

Before the OnData JDBC driver can be used, it must be registered and activated as an OnData client. For each OnData JDBC driver, you can either run the OnData Configurator tool 
included in the OnData JDBC driver release package to register it as a driver client, or create a driver client directly at the OnData Portal and populate the corresponding 
ClientId and ClientSecret information from the OnData Portal to the OnDataDriver.config file. For details on how to register a driver client with the OnData Platform, please refer to 
the [**OnData Client Registration**](reference_doc#securedb/database_drivers/ondata_client_registration).  

## 2. Connecting to Databases Using OnData JDBC Driver
The process to connect to a database using the OnData JDBC Driver varies, depending on the database tools to be used. Below are the sample connection strings to be used with 
the OnData JDBC driver.

  \`\`\`bash
  PostgreSQL:
  jdbc:postgresql://192.168.1.101:5432/db_name?user=xxxxx&password=xxxxx
  
  MSSQL:
  jdbc:sqlserver://192.168.1.101:1433;encrypt=false;databaseName=xxxxx;user=xxxxx;password=xxxxx
  \`\`\`
  
## 3. Using OnData JDBC Driver in Applications

### 3.1 Add Classpath to com.ondata.jdbc Package

In your development project, add a Classpath to the OnData JDBC JAR file **ondata-jdbc-<version_number>-jar-with-dependencies.jar** downloaded from the OnData Portal, as illustrated below.

![Add Classpath](${ img0 })

**Note**: If the JDBC classpath of the default database engine has been added to your project, please remove it.

### 3.2 Configure OnDataDriver.config File

The OnData JDBC driver will look for the OnDataDriver.config file in the same directory where the ondata-jdbc-<version_number>-jar-with-dependencies.jar file is located. The 
OnDataDriver.config file contains the following configuration entries:

- The type of the database (MSSQL, ORACLE, POSTGRESQL, SNOWFLAKE, DB2, MYSQL, or MARIADB)
- The full path to the jar package for the default database driver
- The ApplicationId, which should always be ondata-data-driver
- The optional path where the log files should be stored
- The optional log level (NONE, FATAL, ERROR, WARNING, INFO)
- The optional flag on whether the audit log is enabled
- The optional flag on whether the data masking rules configured at the OnData Portal should be applied to sensitive data when a user does have access to such data
- The optional configuration of operating mode, which can be either Encryption or Decryption (default to Decryption). If the sensitive data inside the database is already 
encrypted, use the Decryption mode. The sensitive data will be dynamically decrypted for authorized users, or masked for unauthorized users if data masking is enabled. 
If the sensitive data inside the database is not encrypted, use the Encryption mode. The sensitive data will be dynamically encrypted, or masked if data masking is enabled, 
for unauthorized users. 
- The optional configuration of operating environment, which can be Production or NonProduction (default to NonProduction). Only if the driver is running with an encrypted 
database in Production environment, use the Production configuration. Otherwise, use the NonProduction configuration.
- The optional configuration for SharedSecret clients. When it is inconvenient or impossible to run the Client Registration process, you may create a shared secret for 
the client at the OnData Portal. Go to \`Settings\` -> \`Organization\` -> \`Clients\`, and under the \`Database Driver\` tab click on the \`Add New...\` button to add a new 
SharedSecret client. Please copy the \`ClientId\` and \`ClientSecret\` and use such information in the OnDataDriver.config file. Please note that once a combination of 
\`ClientId\` and \`ClientSecret\` is specified, the client registration information, if available, is not used anymore.  
- The optional network proxy information. If all of your network traffic goes through a network proxy, you can specify such settings here, so that the OnData database 
driver can still connect to the OnData Portal to retrieve metadata.  
  
**OnDataDriver.config**:
\`\`\`properties
###### Configuration for OnDataDriver ################

# The database type (MSSQL, ORACLE, POSTGRESQL, SNOWFLAKE, DB2, MYSQL, or MARIADB)
DatabaseType = MSSQL
DriverFullPath = file:/C:/driverjars/mssql-jdbc-9.4.1.jre11.jar

#-----------------------------------------------------
# Please do not change this entry
#-----------------------------------------------------
ApplicationId = ondata-data-driver

#-----------------------------------------------------
# A folder to store log files. Please make sure the 
# folder exists and the calling process has read 
# and write permissions to that folder
#-----------------------------------------------------
LogPath = C:\\\\Temp\\\\OnData\\\\

#-----------------------------------------------------
# Logging Levels
# 0: None
#	1: Fatal
#	2: Error
#	3: Warning
#	4: Info
#-----------------------------------------------------
LogLevel = 2

#-----------------------------------------------------
# To enable audit log for any access to encrypted 
# data, please set this option to true
#-----------------------------------------------------
EnableAuditLog = false

#-----------------------------------------------------
# To enable masking rule for encryption / decryption 
# default is true, when set to false, masking rule will
# be ignored even you have configured in OnData Portal
#-----------------------------------------------------
EnableMasking = true

#-----------------------------------------------------
# OperatingMode is Decryption or Encryption, default to Decryption
#-----------------------------------------------------
OperatingMode = Decryption

#-----------------------------------------------------
# OperatingEnvironment is Production or NonProduction, default to NonProduction
#-----------------------------------------------------
OperatingEnvironment = NonProduction

#-----------------------------------------------------
# Get ClientId and ClientSecret in OnData Portal
# Settings > Organization > CLIENTS > Add New to add a SharedSecret client
#-----------------------------------------------------
#ClientId = xxxx
#ClientSecret = xxxx

#-----------------------------------------------------
# Network proxy configuration
# This is for optional proxy setting
# AuthType(0-NoAuth, 1-BasicAuth, 2-DigestAuth, 3-BearerTokenAuth)
# (If AuthType is 3, put token in Password)
#-----------------------------------------------------
#Host = 127.0.0.1
#Port = 7890
#AuthType = 0
#Username = xxxx
#Password = xxxx

\`\`\`

### 3.3 Use OnData JDBC Driver to Create Connection 

To create a connection using the OnData JDBC driver, first use "Class.forName" to create an instance of com.ondata.jdbc.OnDataJDBCDriver. Then use the DriverManager.getConnection() method, 
passing in a database connection string, to get a database connection through the OnData JDBC driver.
  
Below is the sample code for connecting to a database using the OnData JDBC driver.  

\`\`\`java
/**
  * creates connection
  * 
  * @return
  */
private Connection connect() {
  try {
    Class.forName("com.ondata.jdbc.OnDataJDBCDriver").newInstance();
  } catch (Exception e) {
    e.printStackTrace();
  } 

  try {
    return DriverManager.getConnection("jdbc:oracle:thin:demo/demopwd@localhost:1521/XE");
  } catch (SQLException e) {
    e.printStackTrace();
    return null;
  }
}
\`\`\`

### 3.4 Set User Token

In order to manage the data access for each user signing into your application, the application must be able to generate or get user tokens upon user sign in. A user token is a 
JWT (JSON Web Token) which contains a list of data access roles. The data access roles are configured at the OnData Portal and can represent the read and write permissions 
for different data classifications. In your application, you can set a user token to a **Statement** or **PreparedStatement** and then this statement will use that user token to 
access data through the OnData driver. It is recommended to set a user token per statement created from a connection.

![Set user token](${ img1 })

A user token can be either generated by your application itself or obtained from a commercial off-the-shelf Identity and Access Management (IAM) system, such as Azure AD and Okta.

#### 3.4.1 Generate User Token in Application
If you choose to generate the user tokens through your application, please follow the steps below:

- Generate s RSA key pair, upload the public key to OnData Portal (under Settings->Encryption->Keys) as the token validation key. OnData driver will use it to validate the user token.
- Figure out the data access roles of the login user. You can get the user info from HttpContext or Session, then determine the data access roles assigned to that login user. If the connection 
is used to process some background tasks that are not associated with any specific user, then determine data access roles needed for that background process.
- Generate a JWT with the RSA private key, and add an attribute called dataAccessRoles, which is an array of data access roles, as a claim to the payload. It is recommended that the 
username is also added to the JWT token in case you want to log data access history.
- The com.ondata.jdbc.Statement provides a method to pass the user token to the Statement (com.ondata.jdbc.PreparedStatement inherits from com.ondata.jdbc.Statement).  

Below is the sample code for generating a user token for a given username and associated data access roles.  
  
\`\`\`java
import javax.crypto.spec.SecretKeySpec;
import javax.xml.bind.DatatypeConverter;
import java.security.Key;
import java.util.Date;
import java.util.HashMap;
import java.util.List;
import java.util.Map;
import java.util.UUID;
import io.jsonwebtoken.*;
import io.jsonwebtoken.Jwts;
import io.jsonwebtoken.Claims;

public class JWTDemo {

	// The private key. This should be in a property file and not hard coded or under source control
  // It is included here for illustration purpose
  private static String SECRET_KEY = "oeRaYY7Wo24sDqKSX3IM9ASGmdGPmk......";

  // Sample method to construct a JWT
  public static String generateUserToken(List<String> dataAccessRoles, String issuer, String username) {
    
    //The JWT signature algorithm to sign the token
    SignatureAlgorithm signatureAlgorithm = SignatureAlgorithm.RS256;

    long nowMillis = System.currentTimeMillis();
    Date now = new Date(nowMillis);

    byte[] apiKeySecretBytes = DatatypeConverter.parseBase64Binary(SECRET_KEY);
    Key signingKey = new SecretKeySpec(apiKeySecretBytes, signatureAlgorithm.getJcaName());

    // Add the data access roles to the claims
    Map<String, Object> claimsMap= new HashMap<String, Object>();
    claimsMap.put("dataAccessRoles", dataAccessRoles);
    
    JwtBuilder builder = Jwts.builder().setId(UUID.randomUUID().toString())
      .setIssuedAt(now)
      .setSubject(username)
      .setIssuer(issuer)
      .setClaims(claimsMap)
      .signWith(signatureAlgorithm, signingKey);

    // Set the token expiration time to 30 minutes
    long expMillis = nowMillis + 30 * 60 * 1000;
    Date exp = new Date(expMillis);
    builder.setExpiration(exp);

    // Build the JWT and serializes it to a compact, URL-safe string
    return builder.compact();
  }
}
\`\`\`

#### 3.4.2 Get User Token from IAM System
If your application is integrated with an Identity and Access Management (IAM) system for user authentication and authorization management, please follow the steps below:  
- Add a custom claim named dataAccessRoles into access_token.
- Upload the public key(s) of the IAM System to the OnData Portal (under Settings->Encryption->Keys) as token validation key(s).
- Get access_token from the HttpContext or Session object.
- The com.ondata.jdbc.Statement provides a method to pass the user tokens to the Statement instance.  

Below is the sample code to get the access_token from a session object and pass it to a  PreparedStatement object.  
  
\`\`\`java
String userToken = (String)session.getAttribute("access_token");
Connection conn = MyUtil.createDBConnection(connStr);
PreparedStatement ps = null;
ResultSet rs = null;
try {
    String sql = "​select * from dbo.test_float_number where numeric_numer = ?";
    ps = conn.prepareStatement(sql);
    ((com.ondata.jdbc.OnDataPreparedStatement)ps).setUserToken(userToken);
    ps.setBigDecimal(1, new BigDecimal("2.0000"));
    rs = ps.executeQuery();
    while(rs.next()) {
      // Read rows from the ResultSet 
    }
  }catch (Exception e) {
    e.printStackTrace();
  } finally {
    MyUtil.closeDBResource(rs, ps, conn);
}

\`\`\`

### 3.5 Possible Exceptions

If something goes wrong, one of the following error codes could be returned. 

\`\`\`java
// Error codes:
NULL_ENCRYPT_CONTROLLER(-1),
FAILED_CONNECT_ONDATA(-2),
FAILED_PARSE_STATEMENT(-3),
FAILED_EXECUTION_PERMISSION(-4),
STATEMENT_NOT_FOUND(-5),
PARAMETER_NOT_FOUND(-6),
COLUMN_NOT_FOUND(-7),
FAILED_PARAMETER_ENCRYPTION(-8),
FAILED_VALUE_DECRYPTION(-9),
FAILED_INSUFFICIENT_BUFFER(-10),
FAILED_DRIVER_NOT_CONFIGURED(-11);

\`\`\`
`
  
export default content;
